<template lang="pug">
md-layout(md-column='')
  // Page loader
  loader(:loading='loadingComponent', v-if="loadingComponent")
  // Page content
  md-theme(md-name='blue', v-else)
    // Title
    breadcrumbs(section='Playlists', :nav='nav')

    // Form
    md-layout.form-wrapper(:md-gutter='16')
      md-layout(md-flex='100')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          metadata-form(:entity='playlist', :form-submitted='formSubmitted', :validations='$v.playlist', title="")
          playlist-content-table.playlist-content-table(
            :videos='playlist.items',
            label='Playlist content',
            v-if='editMode',
            :deletingItem='deletingItem',
            @removeItem='onItemRemoved'
          )

      md-layout(md-align='center')
        stitch-loader(:loading='creatingOrUpdating', :size='40')
        md-button#submit-btn.md-raised.md-primary(@click='onFormSubmit', v-show='!creatingOrUpdating', :disabled='$v.$pending', md-theme='blue')
          | {{ editMode ? 'Update' : 'Create' }}
</template>

<script>
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import PlaylistContentTable from '../../shared-components/playlist/PlaylistContentTable';
  import playlistService from '../../services/playlistService';
  import StitchLoader from '../../shared-components/Loader';
  import MetadataForm from '../../shared-components/forms/MetadataForm';
  import Breadcrumbs from '../../shared-components/Breadcrumbs';
  import Loader from '../../shared-components/Loader';

  export default {
    mixins: [validationMixin],
    components: {
      PlaylistContentTable,
      MetadataForm,
      StitchLoader,
      Breadcrumbs,
      Loader
    },
    computed: {
      nav() {
        return [
          { title: 'Playlists', route: 'Playlists' },
          { title: this.editMode ? 'Edit Playlist' : 'New Playlist' }
        ]
      },
      editMode() {
        return !!this.$route.params.id;
      }
    },
    data() {
      return {
        loadingComponent: false,
        creatingOrUpdating: false,
        playlistOriginalName: null,
        currentPage: 0,
        formSubmitted: false,
        deletingItem: null,
        playlist: {
          name: '',
          description: '',
          tags: [],
          status: 'public',
          items: []
        }
      };
    },
    validations: {
      playlist: {
        name: {
          required,
          async isUnique(value) {
            const hasToCheck = !this.editMode || value !== this.playlistOriginalName;
            if (value && hasToCheck) {
              const response = await playlistService.getList({ exactName: value, limit: 1 });
              return response.data.total === 0;
            } else {
              return true;
            }
          }
        },
        shortDescription: {
          default:true
        },
        description: {
          default:true
        },
      }
    },
    methods: {
      showAlertToast(text, icon){
        this.$swal({
              text: text,
              position: 'bottom-left',
              icon: icon,
              toast: true,
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              }
            )
      },
      async onItemRemoved(index) {
        this.deletingItem = index;
        const items = this.playlist.items.filter((item, itemIndex) => itemIndex !== index);
        try {
          await playlistService.update(this.playlist._id, Object.assign({}, this.playlist, { items }));
          this.deletingItem = null;
          this.playlist.items.splice(index, 1);
          this.showAlertToast('Playlist item deleted successfully.', 'success');
        } catch (error) {
          this.deletingItem = null;
          this.showAlertToast('Playlist item could not be deleted. Please, try again later.', 'error');
        }
      },

      getPlaylistData() {
        const data = Object.assign({}, this.playlist);
        if (this.editMode) {
          Object.assign(data, { _id: this.$route.params.id });
        }
        return data;
      },

      async createPlaylist() {
        this.creatingOrUpdating = true;
        const data = this.getPlaylistData();
        try {
          await this.$store.dispatch('playlist/create', data);
          this.showAlertToast('Playlist created successfully', 'success');
          this.$delayedNavigation(this.$router, 'Playlists');
        } catch (error) {
          this.showAlertToast('Playlist could not be created. Please, try again later.', 'error');
        } finally {
          this.creatingOrUpdating = false;
        }
      },

      async updatePlaylist() {
        this.creatingOrUpdating = true;
        this.playlist.status = 'public';
        const data = this.getPlaylistData();
        try {
          await playlistService.update(this.$route.params.id, data);
          this.showAlertToast('Playlist updated successfully', 'success');
          this.$delayedNavigation(this.$router, 'Playlists');
        } catch (error) {
          this.showAlertToast('Playlist could not be updated. Please, try again later.', 'error');
        } finally {
          this.creatingOrUpdating = false;
        }

      },

      onFormSubmit() {
        this.formSubmitted = true;
        if (!this.$v.$invalid) {
          if (this.editMode) {
            this.updatePlaylist();
          } else {
            this.createPlaylist();
          }
        }
      },
    },
    async mounted() {
      if (this.editMode) {
        this.loadingComponent = true;
        try{
          const response = await playlistService.get(this.$route.params.id);
          this.playlist = response.data;
          this.playlistOriginalName = this.playlist.name;

          while(this.currentPage+1 < response.data.totalPages){
              this.currentPage++;
              const response = await playlistService.get(this.$route.params.id, {page: this.currentPage});
              this.playlist.items.push(...response.data.items);
            }
        } catch (error) {
          this.showAlertToast('Playlist data could not be retrieved. Please, try again later.', 'error');
        } finally {
          this.loadingComponent = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
.form-wrapper{
  padding: 0 10% !important;
}
.form-wrapper .form-wrapper-frame{
  padding: 10px;
  margin: auto !important;
  margin-top: 20px !important;
  border-radius: 10px;
  box-shadow: 1px 1px 20px 5px rgba(0, 0, 0, 0.2);
}
  .form-wrapper {
    width: 100%;
    padding: 25px 20%;
    @media (max-width: 1200px) {
      padding: 10px .5%;
      margin-left: 0;
    }
    .form-wrapper-frame {
      width: 100%;
      padding: 25px;
    }
  }
  .title-icon {
    margin-top: -5px;
  }
  #submit-btn {
    margin: 25px 0;
    width: 33%;
    padding: 6px;
    font-size: 1.1em;
  }
  .playlist-content-table {
    padding: 24px 0;
  }
</style>



// WEBPACK FOOTER //
// src/spa/playlist/PlaylistForm.vue